<template>
<div class="clone-metric clone-metric--trade">
  <!-- The name of the card, where the card is given one. Without it the first box's own uppercase
       label is the whole of it — one figure named, the other beside it — which is what a card that
       states the source model's own spend needs and all it needs. A card whose boxes are two
       readings of one level names itself once at the top and names each box in the small grey the
       revenue card uses, so that "Media spend" is the section and "Total spend" is one of the two
       things inside it rather than the card said twice. -->
  <div v-if="label" class="clone-metric__label">
    <span class="clone-metric__dot" :style="{ backgroundColor: spendColor }"></span>
    <span>{{ label }}</span>
    <!-- The unit the figures inside are in, beside the name rather than inside each of them: the
         same place every other section on this page names its own. -->
    <span v-if="unit" class="clone-metric__unit">{{ unit }}</span>
  </div>

  <!-- The two figures and the curve they are read off. Stacked, the curve explains the pair after
       the fact; SPLIT, it stands beside them at half the card, on the same row as the boxes it
       explains — see clone-trade-body--split. -->
  <div
    class="clone-trade-body"
    :class="{ 'clone-trade-body--split': split, 'clone-trade-body--wide': split && !roas }">
    <div class="clone-pair">
      <!-- `title` is optional and is a statement where it is given: the clone's media spend is the
           source model's own, which the box says by standing on its own, and the composer's is a share
           of a revenue nobody measured, which has to be said. -->
      <div class="clone-pair__half" :title="spend.title">
        <div class="clone-metric__label" :class="{ 'clone-metric__label--ask': label }">
          <span v-if="!label" class="clone-metric__dot" :style="{ backgroundColor: spendColor }"></span>
          <span>{{ spend.label }}</span>
          <span v-if="spend.factor" class="clone-metric__factor">{{ spend.factor }}</span>
        </div>
        <v-text-field
          ref="spendField"
          :key="spend.inputKey"
          :value="spend.value"
          class="clone-metric__input"
          :disabled="spend.disabled"
          :placeholder="spend.disabled ? '—' : ''"
          hide-details
          single-line
          @change="$emit('spend-input', $event)"
        ></v-text-field>
      </div>

      <!-- The Total ROAS this EKRM will report once it is built, simulated off its own curves and
           spend. It is not a number that can be set — with the curves fixed it is what the spend
           produces — so typing one solves for the spend that produces it.

           A CARD MAY SAY IT ON THE CHART INSTEAD, and one that does passes no `roas` and grows no
           second box: the figure is a coordinate of the curve beside it, and read off the plot it
           costs no width at all — see `now`, which is the same figure and the same event. -->
      <div v-if="roas" class="clone-pair__half" :title="roas.title">
        <div class="clone-metric__label" :class="{ 'clone-metric__label--ask': label }">
          <span v-if="!label" class="clone-metric__dot" :style="{ backgroundColor: roasColor }"></span>
          <span>{{ roas.label }}</span>
        </div>
        <v-text-field
          :key="roas.inputKey"
          :value="roas.value"
          class="clone-metric__input"
          prefix="≈"
          :suffix="roas.suffix"
          :disabled="roas.disabled"
          :placeholder="roas.disabled ? '—' : ''"
          hide-details
          single-line
          @change="$emit('roas-input', $event)"
        ></v-text-field>
      </div>
    </div>

    <!-- The two of them at once. The curve is every level of spend these channels can be read at
         against what each one returns, so a point on it is not one figure with the other derived
         from it: it is the pair, at one level. It is also the only place the shape is visible — that
         the return climbs, peaks and falls is why a Total ROAS can be out of reach at all, and it is
         better seen than explained after the fact. -->
    <div v-if="plot || now" class="clone-trade">
      <!-- The chart's own name, where the card gives it one, in the same small grey the two boxes
           beside it are named in: three things inside one section, each said once and in one voice.
           Like theirs it carries no colour dot — the curve is not a component of anything drawn
           below, and a dot on a name that ties to nothing is a legend for one thing. -->
      <div v-if="plotLabel || now" class="clone-trade__head">
        <div v-if="plotLabel" class="clone-metric__label clone-metric__label--ask clone-trade__name">{{ plotLabel }}</div>
        <!-- WHAT THE PLAN RETURNS, ON THE CHART OF WHAT EVERY PLAN RETURNS. The curve is drawn for
             every level and the one the form stands at is marked on it; this is that mark's own
             figure, written where the chart is named so it is legible without hunting for the dot,
             and it carries the curve's colour for the same reason the mark does.

             AND IT IS STILL TYPED, by pressing it. With the curves fixed a return is not a figure
             that can be set — it is what the spend produces — so a figure typed here is solved back
             into the spend that produces it, which is the same statement the box that used to stand
             beside the spend made. It is a press rather than a permanent field because it is a
             READING first: what a chart says about the plan should not look like an empty box. -->
        <div v-if="now" class="clone-trade__now" :title="now.title">
          <span class="clone-trade__now-label">{{ now.label }}</span>
          <input
            v-if="editingNow"
            ref="nowField"
            class="clone-trade__now-field"
            :value="now.value"
            :aria-label="now.label"
            @keydown.esc.exact="editingNow = false"
            @keydown.enter.exact.prevent="commitNow"
            @blur="commitNow">
          <button
            v-else
            type="button"
            class="clone-trade__now-value"
            :class="{ 'clone-trade__now-value--live': !now.disabled }"
            :style="{ color: roasColor }"
            :disabled="now.disabled"
            @click="openNow">{{ now.disabled ? '—' : `≈${now.value}${now.suffix || ''}` }}</button>
        </div>
      </div>
      <!-- Everything below is the picture, and a card whose curve could not be read carries the
           reading above and nothing else: a plot of a line that does not exist is an empty box where
           a chart was promised. -->
      <template v-if="plot">
      <!-- AND THE WHOLE PLOT IS TAKEABLE. Hovering already names the spend and the return at the
           level under the pointer, which is the offer; clicking accepts it, and the plan lands on
           exactly the point the card just named. It is the same commit the three labelled levels
           under the axis make — they are the levels worth taking without looking, this is any other
           one — so it leaves through the same `jump`. A card that passes no readings has no offer to
           accept and takes no click. -->
      <div
        class="clone-trade__plot"
        :class="{ 'clone-trade__plot--takes': readings.length > 0 }"
        @mousemove="onPointer"
        @mouseleave="hoverAt = null"
        @click="takeReading">
        <svg class="clone-trade__svg" :viewBox="viewBox" preserveAspectRatio="none">
          <path class="clone-trade__area" :d="plot.area"></path>
          <path class="clone-trade__line" :d="plot.line" vector-effect="non-scaling-stroke"></path>
          <rect
            v-for="entry in plot.blocked"
            :key="`blocked_${entry.key}`"
            class="clone-trade__blocked"
            :x="entry.x" y="0" :width="entry.width" :height="height"></rect>
          <line
            v-for="entry in rules"
            :key="`rule_${entry.key}`"
            class="clone-trade__rule"
            :class="`clone-trade__rule--${entry.kind}`"
            :x1="entry.x" :x2="entry.x"
            y1="0" :y2="height" vector-effect="non-scaling-stroke"></line>
          <line
            class="clone-trade__here"
            :x1="plot.currentX" :x2="plot.currentX"
            :y1="plot.currentY" :y2="height" vector-effect="non-scaling-stroke"></line>
        </svg>
        <!-- The marks ride over the svg rather than inside it: the box is stretched to the card's
             width, and a circle drawn in it would arrive as an ellipse. -->
        <span
          v-for="entry in dots"
          :key="`mark_${entry.key}`"
          class="clone-trade__mark"
          :class="`clone-trade__mark--${entry.kind}`"
          :style="entry.style"
          :title="entry.title"></span>
        <span class="clone-trade__mark clone-trade__mark--here" :style="plot.currentStyle"></span>
        <!-- WHAT THE CURVE SAYS WHERE THE POINTER IS, which is the one question a line of every
             level answers and none of its labels do: the reader is at a level, not at a landmark.
             The reading is a point OF the line — the nearest sample to the pointer's x rather than
             the pointer's own y — so the dot rides the curve and the card names a spend and a
             return that actually go together.

             Written here rather than with v-tooltip for the same reason the revenue card's is: that
             tooltip lifts its activator out of the DOM and puts it back beside itself, and this is
             an activator rebuilt on every keystroke. One element over the plot, deaf to the pointer,
             so moving onto it never counts as leaving the plot that opened it. -->
        <template v-if="reading">
          <span class="clone-trade__hover-rule" :style="{ left: reading.left }"></span>
          <span class="clone-trade__mark clone-trade__mark--hover" :style="reading.style"></span>
        </template>
        <div v-if="reading" class="clone-trade-tip" :style="tipStyle">
          <div class="clone-trade-tip__row">
            <span class="clone-trade-tip__dot" :style="{ backgroundColor: spendColor }"></span>
            <span class="clone-trade-tip__name">{{ spend.label }}</span>
            <span class="clone-trade-tip__value">{{ reading.spend }}</span>
          </div>
          <div class="clone-trade-tip__row">
            <span class="clone-trade-tip__dot" :style="{ backgroundColor: roasColor }"></span>
            <span class="clone-trade-tip__name">{{ returnLabel }}</span>
            <span class="clone-trade-tip__value">{{ reading.roas }}</span>
          </div>
        </div>
      </div>
      <!-- The named level, under the axis it sits on. A button, because a chart that says "the most
           is here" and will not take you there has to be read twice; clicking it is the same commit
           as letting the slider go at it. -->
      <div class="clone-trade__ticks" :style="{ height: ticksHeight }">
        <button
          v-for="entry in plot.marks"
          :key="`tick_${entry.key}`"
          type="button"
          class="clone-trade__tick"
          :class="[`clone-trade__tick--${entry.kind}`, { 'clone-trade__tick--edge': entry.edge }]"
          :style="entry.labelStyle"
          :title="entry.title"
          @click="$emit('jump', entry.level)">
          <span v-if="!entry.edge" class="clone-trade__tick-dot"></span>{{ entry.label }}
        </button>
      </div>
      <!-- The control that moves along the line, where a card has one. A card whose only ways in are
           its two boxes and the named levels under the axis passes no slider and grows none: an
           empty track under a chart is a control that does nothing. -->
      <v-slider
        v-if="slider"
        class="clone-trade__slider"
        :value="slider.value"
        :min="slider.min"
        :max="slider.max"
        :step="slider.step"
        :color="spendColor"
        track-color="rgba(0, 0, 0, 0.12)"
        hide-details
        dense
        @input="$emit('slide', $event)"
        @change="$emit('commit', $event)"
      ></v-slider>
      <div class="clone-trade__axis" :class="{ 'clone-trade__axis--bare': !slider }">
        <span>{{ plot.from }}</span>
        <span class="clone-trade__hint">{{ hint }}</span>
        <span>{{ plot.to }}</span>
      </div>
      </template>
    </div>
  </div>

  <!-- WHATEVER ELSE IS SAID ABOUT THIS SPEND, under the row that states it and inside the same card.
       The composer puts the twelve months here: how the year's figure falls across them and how it
       is shared out between the media, which is a second statement about the number typed above it
       and belongs under that number rather than a page below it. A slot rather than a prop bundle
       for the reason the revenue card's is one — the card owns no arithmetic and owns none of the
       slot's either. A card that passes nothing is unchanged. -->
  <slot name="beneath"></slot>

  <div v-if="foot.length" class="clone-metric__foot">
    <span v-for="entry in foot" :key="entry.label" class="clone-foot-pair" :title="entry.title">
      <span class="clone-metric__foot-label">{{ entry.label }}</span>
      <span class="clone-metric__foot-value">{{ entry.value }}</span>
    </span>
  </div>
</div>
</template>

<script>
// Media Spend and Total ROAS, shared by both EKRM wizards. One card, because with the curves fixed
// they are one number seen twice: what is spent IS what comes back.
//
// It owns no arithmetic — the curve arrives as two paths, every mark arrives with its position and
// its label already placed, the corridor arrives as rectangles, and both figures arrive formatted.
// See EkrmChannelCard for why that rule exists.

// How wide the hover card is. Fixed, because it is centred on a point and then clamped to the plot,
// and both of those are arithmetic on a width that has to be known before the box is drawn.
const TIP_WIDTH = 168

export default {
  name: 'EkrmTradeCard',
  props: {
    // The card's own name, where it has one. Given, the two boxes are named in the small grey the
    // revenue card names its fields in and this is the uppercase label above both; omitted, each box
    // wears its own uppercase label and the card is named by the first of them.
    label: { type: String, default: '' },
    // What the card's figures are counted in, named once on the card's own label. Empty leaves it
    // unnamed, which is what a card whose unit is obvious from its boxes needs.
    unit: { type: String, default: '' },
    // The chart's own name, on the same terms as the two boxes': given, it stands over the plot in
    // the small grey they are named in, so the card's three things are each named once. Omitted, the
    // plot goes unnamed, which is what a card whose only other content is the pair it explains needs.
    plotLabel: { type: String, default: '' },
    // Whether the curve stands BESIDE the two figures rather than under them. It is the same card
    // either way and the same reading — a point on a line both figures are read off — but on a wide
    // card the stacked form leaves the boxes alone on a row and pushes the slider that moves them a
    // plot's height away from the numbers it writes.
    split: { type: Boolean, default: false },
    // { label, value, factor, disabled, inputKey, title }. Disabled where the figure cannot be
    // stated yet rather than where it is merely unknown — the composer's spend is bounded by a
    // revenue that may not have been typed, and a box that takes a number it has nowhere to put it
    // is a box that will have to throw it away.
    spend: { type: Object, required: true },
    // { label, value, suffix, disabled, inputKey, title }, or null on a card that says the return on
    // the chart instead — see `now`.
    roas: { type: Object, default: null },
    // The same figure, read off the plot rather than boxed beside the spend: { label, value, suffix,
    // disabled, title }. Written in the plot's own header, pressed to be typed into, and committed
    // through the same `roas-input` a box would have emitted. Null on a card that carries the box.
    now: { type: Object, default: null },
    // { area, line, blocked, marks, currentX, currentY, currentStyle, from, to } or null, and
    // optionally `readings`: the samples the line is drawn from, each already projected and already
    // formatted — [{ at, left, style, spend, roas }] — which is what the hover names. A caller that
    // passes none gets no hover, the same way one that passes no slider gets no slider.
    plot: { type: Object, default: null },
    dots: { type: Array, default: function () { return [] } },
    // The slider under the axis, where the card has one. Null on a card whose ways in are its two
    // boxes and the named levels under the chart.
    slider: { type: Object, default: null },
    viewBox: { type: String, required: true },
    height: { type: Number, required: true },
    ticksHeight: { type: String, default: '0px' },
    hint: { type: String, default: '' },
    spendColor: { type: String, default: '#4B67B8' },
    roasColor: { type: String, default: '#7E57C2' },
    // [{ label, value, title }]
    foot: { type: Array, default: function () { return [] } }
  },
  data () {
    return {
      // Where along the axis the pointer is, as a fraction of the plot's width, or null. It is the
      // only state this card owns and it is not a value of anything: nothing is committed by moving
      // a mouse across a chart.
      hoverAt: null,
      // Whether the reading in the plot's header is open as a field. It is the one other thing this
      // card owns, and it is not a value either: what is typed leaves through `roas-input` the
      // moment it is committed, and the figure shown is always the parent's.
      editingNow: false
    }
  },
  watch: {
    // A figure that has moved closes the field that moved it, the same way the parent's re-key
    // discards a rejected one: what stands there afterwards is what the form carries, and a field
    // left open over it would be showing the number that was asked for beside the one that landed.
    'now.value' () {
      this.editingNow = false
    }
  },
  computed: {
    // What the second figure is called, wherever the card names it: the box beside the spend where
    // there is one, the reading in the plot's header where the chart carries it instead. One name,
    // because it is one figure — the hover card names it too, and a card whose tooltip disagreed
    // with the reading above it would be two names for one line.
    returnLabel () {
      if (this.roas) return this.roas.label
      return this.now ? this.now.label : ''
    },
    // The rules that cross the plot. A mark standing ON the frame passes rule: false — a dashed line
    // drawn at x = 0 is half a line at the edge of the box and reads as a boundary inside the chart
    // rather than as the end of it. Callers that say nothing get a rule for every mark, which is
    // what a corridor drawn inside its own window needs.
    rules () {
      if (!this.plot) return []
      return this.plot.marks.filter(entry => entry.rule !== false)
    },
    readings () {
      return this.plot && this.plot.readings ? this.plot.readings : []
    },
    // The sample the pointer is over: the nearest one along the axis, so what the card names is a
    // point of the curve rather than wherever the mouse happens to be in the box.
    reading () {
      if (this.hoverAt === null || !this.readings.length) return null
      let best = this.readings[0]
      let gap = Math.abs(best.at - this.hoverAt)
      this.readings.forEach(entry => {
        const distance = Math.abs(entry.at - this.hoverAt)
        if (distance < gap) {
          gap = distance
          best = entry
        }
      })
      return best
    },
    // Over the point and centred on it, then held between the plot's two edges: a card hanging off
    // the left of the chart belongs to no picture at all. Same clamp the revenue card's is under.
    tipStyle () {
      if (!this.reading) return null
      return {
        width: `${TIP_WIDTH}px`,
        left: `clamp(0px, calc(${this.reading.left} - ${TIP_WIDTH / 2}px), calc(100% - ${TIP_WIDTH}px))`
      }
    }
  },
  methods: {
    // The caret put in the spend box with what is already there selected, for the step that opens on
    // this card: the figure is seeded at the peak of the curves, and a seeded figure is one the
    // reader either accepts or types over, so it is offered ready to be typed over. Called from the
    // page rather than done here — the card does not know when its step is reached.
    focusValue () {
      const field = this.$refs.spendField
      if (!field || (this.spend && this.spend.disabled)) return
      field.focus()
      const el = field.$refs.input
      if (el) el.select()
    },
    openNow () {
      if (!this.now || this.now.disabled) return
      this.editingNow = true
      this.$nextTick(() => {
        const field = this.$refs.nowField
        const el = Array.isArray(field) ? field[0] : field
        if (el) {
          el.focus()
          el.select()
        }
      })
    },
    // What was typed, as text, to the parent that formatted what was there — the same contract the
    // boxes have. The field closes on the way out whether or not anything moves: a return that
    // cannot be reached is answered in a note above the cards, not by a box left standing open.
    commitNow (event) {
      if (!this.editingNow) return
      this.editingNow = false
      const value = event && event.target ? event.target.value : ''
      if (this.now && value !== this.now.value) this.$emit('roas-input', value)
    },
    // The level under the pointer, taken. Nothing happens where there is no reading — a plot with
    // no samples behind it, or a click that arrives before the pointer has ever moved over it.
    takeReading () {
      const reading = this.reading
      if (!reading || !isFinite(Number(reading.level))) return
      this.$emit('jump', reading.level)
    },
    onPointer (event) {
      const box = event.currentTarget.getBoundingClientRect()
      if (!(box.width > 0)) return
      const at = (event.clientX - box.left) / box.width
      this.hoverAt = Math.min(1, Math.max(0, at))
    }
  }
}
</script>

<style scoped>
  /* The card's own chrome, on its own root. The page it sits in owns the LAYOUT — how wide it is
     and how it wraps — because that is a statement about the row, not about the card. */
  .clone-metric {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 12px 16px 10px;
  }

  /* The two figures that are one number seen twice, side by side above the control that moves them.
     They wrap on a narrow card rather than squeezing the digits. */
  .clone-pair {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
  }
  .clone-pair__half {
    flex: 1 1 160px;
    min-width: 0;
  }

  /* SPLIT: the two figures on one half of the row and the curve they are read off on the other,
     which is the form a wide card takes. The figures come first, on the left, because they are what
     the card asks for and the chart is what explains them — the wizard reads left to right and the
     answer is not the picture. Below the card's own breakpoint the halves fall back into the stack
     they are in everywhere else, a 50% plot and a 50% pair of ten-digit boxes being two things
     neither of which fits. */
  .clone-trade-body--split {
    display: flex;
    align-items: flex-start;
    gap: 4px 24px;
    flex-wrap: wrap;
  }
  .clone-trade-body--split > .clone-pair {
    flex: 1 1 320px;
    min-width: 0;
  }
  .clone-trade-body--split > .clone-trade {
    flex: 1 1 320px;
    min-width: 0;
    margin-top: 0;
  }

  /* WITH THE RETURN READ OFF THE CHART, the pair is one box and the plot takes the room the second
     one was standing in. The figure did not get smaller by being moved — it got a curve under it —
     so the width it cost goes to the picture it is now a point of, and the boxes keep only what a
     ten-digit field needs. */
  .clone-trade-body--wide > .clone-pair {
    flex: 0 1 240px;
  }
  .clone-trade-body--wide > .clone-trade {
    flex: 3 1 380px;
  }

  /* The chart's name and the plan's own reading of it, on one line: what is drawn, and what it says
     where the form is standing. They are the two ends of the same row so the figure lands over the
     right of the plot, clear of the curve's own opening. */
  .clone-trade__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }
  .clone-trade__now {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }
  .clone-trade__now-label {
    font-size: 0.7rem;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.55);
    white-space: nowrap;
  }
  /* The figure itself, in the curve's colour and in the size a figure is read at: it is the answer
     the card exists to give, and it was in a box of its own until the chart was made to say it.
     Written as a button and drawn as a number — the browser's chrome is stripped back — so what
     invites the press is the dotted rule under it rather than a control around it. */
  .clone-trade__now-value {
    padding: 0;
    border: none;
    background: none;
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.3;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    cursor: default;
  }
  .clone-trade__now-value--live {
    cursor: pointer;
    border-bottom: 1px dotted rgba(0, 0, 0, 0.3);
  }
  .clone-trade__now-value--live:hover,
  .clone-trade__now-value--live:focus-visible {
    border-bottom-style: solid;
    outline: none;
  }
  /* And the same figure, typed. It stands exactly where the reading was and in the same size, so the
     press that swaps one for the other changes nothing but the box around the number. */
  .clone-trade__now-field {
    width: 84px;
    padding: 0 2px;
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.3;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #7E57C2;
    background-color: #ffffff;
    border: none;
    border-bottom: 1px solid #7E57C2;
    border-radius: 0;
    outline: none;
  }

  /* The trade-off itself: Total ROAS against Media Spend, over every level these channels can be
     read at, with the slider that moves along it on the same axis directly underneath. */
  .clone-trade {
    margin-top: 10px;
  }
  .clone-trade__plot {
    position: relative;
  }
  .clone-trade__svg {
    display: block;
    width: 100%;
    height: 64px;
  }
  .clone-trade__area {
    fill: rgba(126, 87, 194, 0.12);
  }
  .clone-trade__line {
    fill: none;
    stroke: #7E57C2;
    stroke-width: 1.5;
    stroke-linejoin: round;
  }
  /* Where these curves return the most, where the wizard stops accepting the spend, and where this
     plan is. */
  .clone-trade__rule {
    stroke-width: 1;
    stroke-dasharray: 2 2;
  }
  /* Grey and hollow, where the plan's own mark below is solid and blue: these are landmarks on the
     chart and not figures the payload carries. */
  .clone-trade__rule--peak {
    stroke: rgba(0, 0, 0, 0.25);
  }
  .clone-trade__rule--limit {
    stroke: rgba(0, 0, 0, 0.3);
    stroke-dasharray: 3 2;
  }
  /* The ground outside what the wizard will accept. Over the curve rather than under it, so the line
     is visibly greyed out there and not merely standing on a grey floor. */
  .clone-trade__blocked {
    fill: rgba(0, 0, 0, 0.07);
  }
  .clone-trade__here {
    stroke: #4B67B8;
    stroke-width: 1;
  }
  /* The marks sit over the svg and not inside it: the box is stretched to the card's width, so a
     circle drawn in it would arrive as an ellipse. */
  .clone-trade__mark {
    position: absolute;
    width: 9px;
    height: 9px;
    margin: -4.5px 0 0 -4.5px;
    border-radius: 50%;
    pointer-events: none;
  }
  .clone-trade__mark--here {
    background-color: #4B67B8;
    box-shadow: 0 0 0 2px #ffffff;
  }
  .clone-trade__mark--peak {
    background-color: #ffffff;
    border: 1.5px solid rgba(0, 0, 0, 0.35);
    pointer-events: auto;
  }
  /* The point the pointer is over. In the curve's own colour and hollow, so it reads as a place on
     the line being looked at rather than as a level the plan has been moved to. */
  /* The plot is one big control where the card was given samples to read: the pointer says so
     before the click does. */
  .clone-trade__plot--takes {
    cursor: pointer;
  }

  .clone-trade__mark--hover {
    background-color: #ffffff;
    border: 2px solid #7E57C2;
  }
  /* And the level it stands at, down to the axis: a reading of a chart is a reading at an x, and
     without the line the two figures in the card belong to a dot the eye has to find. */
  .clone-trade__hover-rule {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0;
    border-left: 1px dashed rgba(126, 87, 194, 0.55);
    pointer-events: none;
  }

  /* WHAT THE CURVE SAYS AT THAT LEVEL, in a card rather than a line: the spend and the return that
     go together there, each beside the colour its box wears. Above the plot, because a card
     explaining a point may not stand on it, and deaf to the pointer so moving onto it never counts
     as leaving the chart that opened it. The same card the revenue row's columns open. */
  .clone-trade-tip {
    position: absolute;
    bottom: calc(100% + 8px);
    z-index: 2;
    padding: 7px 11px 8px;
    border-radius: 4px;
    color: #ffffff;
    background-color: rgba(33, 33, 33, 0.94);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.26);
    pointer-events: none;
  }
  .clone-trade-tip__row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    line-height: 1.6;
  }
  .clone-trade-tip__dot {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .clone-trade-tip__name {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
  }
  /* The figures in one column and of one width, so two amounts read as a column of amounts rather
     than as two sentences of different lengths. */
  .clone-trade-tip__value {
    flex: none;
    font-variant-numeric: tabular-nums;
  }

  /* The name of that level, centred under its own mark and carrying the glyph of it, so what the
     mark on the line means needs no reading. */
  .clone-trade__ticks {
    position: relative;
    height: 15px;
    margin-top: 1px;
  }
  .clone-trade__tick {
    position: absolute;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: none;
    font-size: 0.62rem;
    line-height: 15px;
    white-space: nowrap;
    color: rgba(0, 0, 0, 0.5);
    cursor: pointer;
  }
  .clone-trade__tick:hover,
  .clone-trade__tick:focus {
    color: rgba(0, 0, 0, 0.85);
    text-decoration: underline;
    outline: none;
  }
  /* An end of the axis is not a point on it: it stands ON the frame, so it is written against the
     edge it names rather than centred on a mark it does not have, and carries no glyph — there is
     nothing on the line for a glyph to repeat. */
  .clone-trade__tick--edge {
    transform: none;
  }
  .clone-trade__tick-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex: none;
  }
  .clone-trade__tick--peak .clone-trade__tick-dot {
    background-color: #ffffff;
    border: 1.5px solid rgba(0, 0, 0, 0.35);
  }
  /* A limit is a line and not a point, and its glyph says so. */
  .clone-trade__tick--limit .clone-trade__tick-dot {
    width: 2px;
    height: 9px;
    border-radius: 0;
    background-color: rgba(0, 0, 0, 0.35);
  }
  /* The slider's track runs the full width of the box, which is the same width the plot is drawn
     across, so the thumb sits under the point it moves. */
  .clone-trade__slider {
    margin-top: -6px;
  }
  .clone-trade__slider >>> .v-slider {
    margin: 0;
  }
  .clone-trade__axis {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.45);
    margin-top: -4px;
  }
  /* With no slider between them, the axis follows the labels directly and needs the air the slider's
     own negative margins were taking back. */
  .clone-trade__axis--bare {
    margin-top: 2px;
  }
  .clone-trade__hint {
    color: rgba(0, 0, 0, 0.35);
  }
  /* The chart's name sits over the plot the way each box's sits over its field, so the three names
     on a split card land on one line. */
  .clone-trade__name {
    margin-bottom: 2px;
  }

  .clone-metric__label {
    display: flex;
    align-items: center;
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
  }
  /* The unit, beside the section's own name and lighter than it: it names what the figures are
     counted in rather than being one of them. */
  .clone-metric__unit {
    margin-left: 6px;
    text-transform: none;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.38);
  }
  /* What the figure IS, under a card that has already named itself: the same small grey the revenue
     card writes 'Total revenue' and 'Avg. per month' in, so the two cards ask for their figures in
     one voice — and with no colour dot, for the same reason those two carry none. A card that has
     named its own section once does not need each thing inside it to carry a legend. */
  .clone-metric__label--ask {
    letter-spacing: normal;
    text-transform: none;
    margin-top: 2px;
    line-height: 1.3;
  }
  /* Ties each box to the colour its section is drawn in below: the spend stack, the revenue bars
     and the decay plots. */
  .clone-metric__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 7px;
    flex: none;
  }
  /* The editable headline. Sized and weighted like the static value it replaced, with the input
     furniture stripped back so the box still reads as a figure rather than as a form. */
  .clone-metric__input {
    margin: 0;
    padding: 0;
  }
  .clone-metric__input >>> input {
    font-size: 1.6rem;
    font-weight: 500;
    line-height: 1.45;
    padding: 0;
    max-height: none;
  }
  .clone-metric__input >>> .v-text-field__prefix,
  .clone-metric__input >>> .v-text-field__suffix {
    font-size: 1.6rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-metric__input >>> .v-input__slot::before {
    border-color: rgba(0, 0, 0, 0.12);
  }
  /* How far this figure has been moved off the source's own. */
  .clone-metric__factor {
    margin-left: auto;
    font-weight: 500;
    color: #4B67B8;
    letter-spacing: 0;
    text-transform: none;
  }
  .clone-metric__foot {
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 0.75rem;
  }
  .clone-metric__foot-label {
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-metric__foot-value {
    font-weight: 500;
  }
  /* A label and its figure travel together, so a footer can carry two of them and still read as
     two facts rather than four. */
  .clone-foot-pair {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
</style>
